import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Calendar, Trash2, Eye, Edit, Check, Clock, X, ChevronRight, CalendarDays, Users, DollarSign } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const TURKISH_DAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

export default function MealPlanning() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Create form state
  const [planName, setPlanName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [defaultPersonCount, setDefaultPersonCount] = useState(10);
  const [planDays, setPlanDays] = useState([]);
  
  // Day edit state
  const [editingDayIndex, setEditingDayIndex] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [dayMealName, setDayMealName] = useState('');
  const [dayPersonCount, setDayPersonCount] = useState(10);
  const [dayIngredients, setDayIngredients] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, templatesRes, productsRes] = await Promise.all([
        api.get('/meal-plans'),
        api.get('/staff-meal-templates'),
        api.get('/products')
      ]);
      setPlans(plansRes.data);
      setTemplates(templatesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Tarih aralığından günleri oluştur
  const generateDays = () => {
    if (!startDate || !endDate) {
      toast.error('Başlangıç ve bitiş tarihi seçin');
      return;
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (end < start) {
      toast.error('Bitiş tarihi başlangıçtan önce olamaz');
      return;
    }
    
    const days = [];
    const current = new Date(start);
    
    while (current <= end) {
      const dayOfWeek = current.getDay();
      // JavaScript'te Pazar 0, biz Pazartesi'yi 0 yapıyoruz
      const turkishDayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      
      days.push({
        day: TURKISH_DAYS[turkishDayIndex],
        date: current.toISOString().split('T')[0],
        template_id: null,
        meal_name: '',
        person_count: defaultPersonCount,
        ingredients: []
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    setPlanDays(days);
    toast.success(`${days.length} günlük plan oluşturuldu`);
  };

  const handleLoadTemplateToDay = (dayIndex, templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    
    const updatedDays = [...planDays];
    updatedDays[dayIndex] = {
      ...updatedDays[dayIndex],
      template_id: templateId,
      meal_name: template.name,
      ingredients: template.ingredients.map(ing => ({
        product_id: ing.product_id,
        quantity: ing.quantity
      }))
    };
    setPlanDays(updatedDays);
    setEditingDayIndex(null);
    toast.success(`${template.name} şablonu yüklendi`);
  };

  const openDayEditor = (dayIndex) => {
    const day = planDays[dayIndex];
    setEditingDayIndex(dayIndex);
    setDayMealName(day.meal_name || '');
    setDayPersonCount(day.person_count || defaultPersonCount);
    setSelectedTemplateId(day.template_id || '');
    
    // Malzemeleri product bilgileriyle zenginleştir
    const enrichedIngredients = day.ingredients.map(ing => {
      const product = products.find(p => p.id === ing.product_id);
      return {
        product_id: ing.product_id,
        product_name: product?.name || 'Bilinmeyen',
        quantity: ing.quantity,
        unit: product?.unit || 'adet',
        unit_price: product?.unit_price || 0,
        total_cost: ing.quantity * (product?.unit_price || 0)
      };
    });
    setDayIngredients(enrichedIngredients);
  };

  const handleAddIngredientToDay = () => {
    if (!selectedProductId || !selectedQuantity || parseFloat(selectedQuantity) <= 0) {
      toast.error('Ürün seçin ve miktar girin');
      return;
    }
    
    const product = products.find(p => p.id === selectedProductId);
    if (!product) return;
    
    if (dayIngredients.find(i => i.product_id === selectedProductId)) {
      toast.error('Bu ürün zaten eklenmiş');
      return;
    }
    
    setDayIngredients([...dayIngredients, {
      product_id: selectedProductId,
      product_name: product.name,
      quantity: parseFloat(selectedQuantity),
      unit: product.unit,
      unit_price: product.unit_price,
      total_cost: parseFloat(selectedQuantity) * product.unit_price
    }]);
    
    setSelectedProductId('');
    setSelectedQuantity('');
  };

  const handleRemoveIngredientFromDay = (productId) => {
    setDayIngredients(dayIngredients.filter(i => i.product_id !== productId));
  };

  const saveDayChanges = () => {
    if (editingDayIndex === null) return;
    
    const updatedDays = [...planDays];
    updatedDays[editingDayIndex] = {
      ...updatedDays[editingDayIndex],
      meal_name: dayMealName,
      person_count: dayPersonCount,
      template_id: selectedTemplateId || null,
      ingredients: dayIngredients.map(i => ({
        product_id: i.product_id,
        quantity: i.quantity
      }))
    };
    setPlanDays(updatedDays);
    setEditingDayIndex(null);
    toast.success('Gün kaydedildi');
  };

  const clearDay = (dayIndex) => {
    const updatedDays = [...planDays];
    updatedDays[dayIndex] = {
      ...updatedDays[dayIndex],
      template_id: null,
      meal_name: '',
      ingredients: []
    };
    setPlanDays(updatedDays);
  };

  const handleCreatePlan = async () => {
    if (!planName.trim()) {
      toast.error('Plan adı girin');
      return;
    }
    if (planDays.length === 0) {
      toast.error('Önce günleri oluşturun');
      return;
    }
    
    try {
      await api.post('/meal-plans', {
        name: planName,
        start_date: startDate,
        end_date: endDate,
        default_person_count: defaultPersonCount,
        days: planDays
      });
      toast.success('Yemek planı oluşturuldu');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Plan oluşturulamadı');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('Bu planı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/meal-plans/${planId}`);
      toast.success('Plan silindi');
      fetchData();
    } catch (error) {
      toast.error('Plan silinemedi');
    }
  };

  const handleUpdateStatus = async (planId, status) => {
    try {
      await api.put(`/meal-plans/${planId}/status?status=${status}`);
      toast.success('Durum güncellendi');
      fetchData();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const resetForm = () => {
    setPlanName('');
    setStartDate('');
    setEndDate('');
    setDefaultPersonCount(10);
    setPlanDays([]);
    setEditingDayIndex(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const calculateDayCost = (day) => {
    return day.ingredients.reduce((sum, ing) => {
      const product = products.find(p => p.id === ing.product_id);
      return sum + (ing.quantity * (product?.unit_price || 0));
    }, 0);
  };

  const calculateTotalCost = () => {
    return planDays.reduce((sum, day) => sum + calculateDayCost(day), 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Aktif</Badge>;
      case 'completed':
        return <Badge className="bg-gray-500">Tamamlandı</Badge>;
      default:
        return <Badge variant="outline">Taslak</Badge>;
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight">Yemek Planlaması</h1>
            <p className="text-muted-foreground mt-1">Haftalık personel yemek planı oluşturun</p>
          </div>
          
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-plan-btn" onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                Yeni Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Yeni Yemek Planı Oluştur</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Plan Bilgileri */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Plan Adı</Label>
                    <Input
                      value={planName}
                      onChange={(e) => setPlanName(e.target.value)}
                      placeholder="Örn: 25-31 Mart Haftası"
                      data-testid="plan-name-input"
                    />
                  </div>
                  <div>
                    <Label>Başlangıç Tarihi</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div>
                    <Label>Bitiş Tarihi</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      data-testid="end-date-input"
                    />
                  </div>
                  <div>
                    <Label>Varsayılan Kişi Sayısı</Label>
                    <Input
                      type="number"
                      min="1"
                      value={defaultPersonCount}
                      onChange={(e) => setDefaultPersonCount(parseInt(e.target.value) || 10)}
                      data-testid="default-person-count"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={generateDays} className="w-full" data-testid="generate-days-btn">
                      <CalendarDays className="w-4 h-4 mr-2" />
                      Günleri Oluştur
                    </Button>
                  </div>
                </div>
                
                {/* Günler */}
                {planDays.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-28">Gün</TableHead>
                          <TableHead className="w-28">Tarih</TableHead>
                          <TableHead>Yemek</TableHead>
                          <TableHead className="text-center w-20">Kişi</TableHead>
                          <TableHead className="text-right w-28">Maliyet</TableHead>
                          <TableHead className="w-32 text-center">İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {planDays.map((day, index) => (
                          <TableRow key={index} data-testid={`plan-day-${index}`}>
                            <TableCell className="font-medium">{day.day}</TableCell>
                            <TableCell>{formatDate(day.date)}</TableCell>
                            <TableCell>
                              {day.meal_name || (
                                <span className="text-muted-foreground italic">Henüz belirlenmedi</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">{day.person_count}</TableCell>
                            <TableCell className="text-right">
                              {day.ingredients.length > 0 ? formatCurrency(calculateDayCost(day)) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDayEditor(index)}
                                  data-testid={`edit-day-${index}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {day.meal_name && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => clearDay(index)}
                                    className="text-destructive"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell colSpan={4}>Toplam Tahmini Maliyet</TableCell>
                          <TableCell className="text-right text-lg">{formatCurrency(calculateTotalCost())}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                {/* Gün Düzenleme Dialogu */}
                {editingDayIndex !== null && (
                  <div className="border rounded-lg p-4 bg-blue-50/50 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold">
                        {planDays[editingDayIndex]?.day} - {formatDate(planDays[editingDayIndex]?.date)}
                      </h3>
                      <Button variant="ghost" size="sm" onClick={() => setEditingDayIndex(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Şablon Seçimi */}
                    {templates.length > 0 && (
                      <div>
                        <Label>Şablondan Yükle</Label>
                        <Select onValueChange={(v) => handleLoadTemplateToDay(editingDayIndex, v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Şablon seçin..." />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.name} ({t.ingredients.length} malzeme)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Yemek Adı</Label>
                        <Input
                          value={dayMealName}
                          onChange={(e) => setDayMealName(e.target.value)}
                          placeholder="Yemek adı..."
                        />
                      </div>
                      <div>
                        <Label>Kişi Sayısı</Label>
                        <Input
                          type="number"
                          min="1"
                          value={dayPersonCount}
                          onChange={(e) => setDayPersonCount(parseInt(e.target.value) || 1)}
                        />
                      </div>
                    </div>
                    
                    {/* Malzeme Ekleme */}
                    <div>
                      <Label>Malzeme Ekle</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Ürün seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} - {formatCurrency(p.unit_price)}/{p.unit}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          step="0.01"
                          className="w-24"
                          value={selectedQuantity}
                          onChange={(e) => setSelectedQuantity(e.target.value)}
                          placeholder="Miktar"
                        />
                        <Button onClick={handleAddIngredientToDay}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Malzeme Listesi */}
                    {dayIngredients.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Malzeme</TableHead>
                              <TableHead className="text-right">Miktar</TableHead>
                              <TableHead className="text-right">Maliyet</TableHead>
                              <TableHead className="w-10"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {dayIngredients.map((ing) => (
                              <TableRow key={ing.product_id}>
                                <TableCell>{ing.product_name}</TableCell>
                                <TableCell className="text-right">{ing.quantity} {ing.unit}</TableCell>
                                <TableCell className="text-right">{formatCurrency(ing.total_cost)}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveIngredientFromDay(ing.product_id)}
                                    className="text-destructive"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    
                    <Button onClick={saveDayChanges} className="w-full">
                      <Check className="w-4 h-4 mr-2" />
                      Günü Kaydet
                    </Button>
                  </div>
                )}
                
                {planDays.length > 0 && (
                  <Button onClick={handleCreatePlan} className="w-full" size="lg" data-testid="save-plan-btn">
                    Planı Kaydet
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Plans List */}
        <div className="grid gap-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : plans.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Henüz yemek planı oluşturulmamış
              </CardContent>
            </Card>
          ) : (
            plans.map((plan) => (
              <Card key={plan.id} data-testid={`plan-card-${plan.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        {plan.name}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formatDate(plan.start_date)} - {formatDate(plan.end_date)} • {plan.days.length} gün
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(plan.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{plan.total_persons} kişi</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{formatCurrency(plan.total_estimated_cost)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Kişi başı:</span>
                      <span className="text-sm font-medium">{formatCurrency(plan.avg_cost_per_person)}</span>
                    </div>
                  </div>
                  
                  {/* Günler özeti */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {plan.days.map((day, idx) => (
                      <div
                        key={idx}
                        className={`px-3 py-1 rounded-full text-xs ${
                          day.meal_name 
                            ? 'bg-primary/10 text-primary' 
                            : 'bg-muted text-muted-foreground'
                        }`}
                        title={day.meal_name || 'Belirlenmedi'}
                      >
                        {day.day_name?.substring(0, 3) || day.day?.substring(0, 3)}
                        {day.meal_name && <Check className="w-3 h-3 inline ml-1" />}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2">
                      {plan.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(plan.id, 'active')}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Aktifleştir
                        </Button>
                      )}
                      {plan.status === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(plan.id, 'completed')}
                        >
                          <Clock className="w-4 h-4 mr-1" />
                          Tamamlandı
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setIsViewDialogOpen(true);
                        }}
                        data-testid={`view-plan-${plan.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Detay
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePlan(plan.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-plan-${plan.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* View Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedPlan?.name}</DialogTitle>
            </DialogHeader>
            {selectedPlan && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground">Toplam Kişi</p>
                      <p className="text-xl font-bold">{selectedPlan.total_persons}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground">Toplam Maliyet</p>
                      <p className="text-xl font-bold">{formatCurrency(selectedPlan.total_estimated_cost)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-muted-foreground">Kişi Başı</p>
                      <p className="text-xl font-bold">{formatCurrency(selectedPlan.avg_cost_per_person)}</p>
                    </CardContent>
                  </Card>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Gün</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Yemek</TableHead>
                      <TableHead className="text-center">Kişi</TableHead>
                      <TableHead className="text-right">Maliyet</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedPlan.days.map((day, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{day.day_name}</TableCell>
                        <TableCell>{formatDate(day.date)}</TableCell>
                        <TableCell>{day.meal_name || <span className="text-muted-foreground">-</span>}</TableCell>
                        <TableCell className="text-center">{day.person_count}</TableCell>
                        <TableCell className="text-right">{formatCurrency(day.estimated_cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
